import React, { useState, useEffect } from 'react';
// import { useLanguage } from '@/state/language/LanguageProvider';
import { resourcesService, Resource, ResourceTag, CreateResourceData, UpdateResourceData } from '@/services/resources';
import { 
  XMarkIcon,
  LinkIcon,
  DocumentIcon,
  TagIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

// =====================================================
// RESOURCE FORM MODAL COMPONENT
// TMOS Platform - Resource Creation/Edit Form
// =====================================================
// Component: ResourceFormModal
// Description: Modal form for creating and editing resources
// Author: TMOS Development Team
// Date: September 30, 2025
// =====================================================

interface ResourceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  resource?: Resource | null;
  mode: 'create' | 'edit';
}

export const ResourceFormModal: React.FC<ResourceFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  resource,
  mode
}) => {
  // const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<ResourceTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [validityPeriod, setValidityPeriod] = useState<'1day' | '1week' | '1month' | '1year' | 'custom'>('1month');

  // Form state
  const [formData, setFormData] = useState<CreateResourceData>({
    title: '',
    description: '',
    url: '',
    file_path: '',
    resource_type: 'link',
    active_from: new Date().toISOString().split('T')[0],
    active_until: '',
    tag_ids: []
  });

  // =====================================================
  // INITIALIZATION
  // =====================================================

  useEffect(() => {
    if (isOpen) {
      loadTags();
      if (mode === 'edit' && resource) {
        setFormData({
          title: resource.title,
          description: resource.description || '',
          url: resource.url || '',
          file_path: resource.file_path || '',
          resource_type: resource.resource_type,
          // image_url: resource.image_url || '',
          active_from: resource.active_from.split('T')[0],
          active_until: resource.active_until ? resource.active_until.split('T')[0] : '',
          tag_ids: resource.tags?.map(tag => tag.id) || []
        });
        setSelectedTags(resource.tags?.map(tag => tag.id) || []);
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode, resource]);

  const loadTags = async () => {
    try {
      const tagsData = await resourcesService.getTags();
      setTags(tagsData);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      file_path: '',
      resource_type: 'link',
      active_from: new Date().toISOString().split('T')[0],
      active_until: '',
      tag_ids: []
    });
    setSelectedTags([]);
    setFile(null);
    setError(null);
    setTagInput('');
    setValidityPeriod('1month');
  };

  // Calculate validity period
  const calculateValidityPeriod = (period: string) => {
    const today = new Date();
    const endDate = new Date(today);
    
    switch (period) {
      case '1day':
        endDate.setDate(today.getDate() + 1);
        break;
      case '1week':
        endDate.setDate(today.getDate() + 7);
        break;
      case '1month':
        endDate.setMonth(today.getMonth() + 1);
        break;
      case '1year':
        endDate.setFullYear(today.getFullYear() + 1);
        break;
      case 'custom':
        return formData.active_until;
    }
    
    return endDate.toISOString().split('T')[0];
  };

  // =====================================================
  // FORM HANDLERS
  // =====================================================

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagToggle = (tagId: string) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    setSelectedTags(newSelectedTags);
    setFormData(prev => ({
      ...prev,
      tag_ids: newSelectedTags
    }));
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagInput(value);
    
    // Check for # tags in the input
    const hashtags = value.match(/#\w+/g) || [];
    if (hashtags.length > 0) {
      const lastHashtag = hashtags[hashtags.length - 1];
      const tagName = lastHashtag.substring(1); // Remove #
      
      // Check if tag exists
      const existingTag = tags.find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
      if (existingTag && !selectedTags.includes(existingTag.id)) {
        handleTagToggle(existingTag.id);
      }
    }
  };

  const handleTagInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tagName = tagInput.trim().replace(/^#/, '');
      if (tagName) {
        // Check if tag exists
        const existingTag = tags.find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          if (!selectedTags.includes(existingTag.id)) {
            handleTagToggle(existingTag.id);
          }
        } else {
          // Create new tag
          try {
            const newTag = await resourcesService.createTag(tagName);
            setTags(prev => [...prev, newTag]);
            setSelectedTags(prev => [...prev, newTag.id]);
            setFormData(prev => ({
              ...prev,
              tag_ids: [...(prev.tag_ids || []), newTag.id]
            }));
          } catch (err) {
            console.error('Error creating tag:', err);
          }
        }
        setTagInput('');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFormData(prev => ({
        ...prev,
        resource_type: 'document'
      }));
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;

    try {
      setFileUploading(true);
      const uploadResult = await resourcesService.uploadFile(file);
      setFormData(prev => ({
        ...prev,
        file_path: uploadResult.file_path
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setFileUploading(false);
    }
  };

  const handleValidityPeriodChange = (period: string) => {
    setValidityPeriod(period as any);
    if (period !== 'custom') {
      const endDate = calculateValidityPeriod(period);
      setFormData(prev => ({
        ...prev,
        active_until: endDate
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Upload file if needed and get the file_path directly
      let filePath = formData.file_path;
      if (file && !filePath) {
        try {
          setFileUploading(true);
          const uploadResult = await resourcesService.uploadFile(file);
          filePath = uploadResult.file_path;
          setFormData(prev => ({
            ...prev,
            file_path: filePath
          }));
        } catch (uploadErr: any) {
          setError(uploadErr.message || 'Failed to upload file');
          setLoading(false);
          setFileUploading(false);
          return;
        } finally {
          setFileUploading(false);
        }
      }

      if (!filePath && formData.resource_type === 'document') {
        setError('File path is required for document resources');
        setLoading(false);
        return;
      }

      // Prepare resource data with the file_path
      const resourceData = {
        ...formData,
        file_path: filePath || formData.file_path
      };

      if (mode === 'create') {
        await resourcesService.createResource(resourceData);
      } else if (mode === 'edit' && resource) {
        const updateData: UpdateResourceData = { ...resourceData };
        await resourcesService.updateResource(resource.id, updateData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${mode} resource`);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  const renderTagButton = (tag: ResourceTag) => {
    const isSelected = selectedTags.includes(tag.id);
    return (
      <button
        key={tag.id}
        type="button"
        onClick={() => handleTagToggle(tag.id)}
        className={`px-3 py-1 rounded-full text-sm transition-colors ${
          isSelected
            ? 'bg-blue-100 text-blue-700 border border-blue-300'
            : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
        }`}
      >
        {tag.name}
        {tag.is_predefined && <span className="ml-1 text-xs">★</span>}
      </button>
    );
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Add New Resource' : 'Edit Resource'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Resource Type - First */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resource Type *
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resource_type"
                  value="link"
                  checked={formData.resource_type === 'link'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <LinkIcon className="h-5 w-5 mr-1" />
                External Link
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="resource_type"
                  value="document"
                  checked={formData.resource_type === 'document'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <DocumentIcon className="h-5 w-5 mr-1" />
                Document
              </label>
            </div>
          </div>

          {/* Title - Second */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter resource title"
            />
          </div>

          {/* Validity Period - Third */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CalendarIcon className="h-4 w-4 inline mr-1" />
              Validity Period
            </label>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '1day', label: '1 Day' },
                  { value: '1week', label: '1 Week' },
                  { value: '1month', label: '1 Month' },
                  { value: '1year', label: '1 Year' },
                  { value: 'custom', label: 'Custom' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleValidityPeriodChange(option.value)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      validityPeriod === option.value
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {validityPeriod === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      name="active_from"
                      value={formData.active_from}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Until</label>
                    <input
                      type="date"
                      name="active_until"
                      value={formData.active_until}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Description - Fourth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter resource description"
            />
          </div>

          {/* URL (for links) - Fifth */}
          {formData.resource_type === 'link' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL *
              </label>
              <input
                type="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>
          )}

          {/* File Upload (for documents) - Fifth */}
          {formData.resource_type === 'document' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document File *
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.pptx,.ppt"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {file && (
                  <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <span className="text-sm text-gray-600">{file.name}</span>
                    <button
                      type="button"
                      onClick={handleFileUpload}
                      disabled={fileUploading}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {fileUploading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
                {formData.file_path && (
                  <p className="text-sm text-green-600">✓ File uploaded successfully</p>
                )}
              </div>
            </div>
          )}

          {/* Tags - Sixth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <TagIcon className="h-4 w-4 inline mr-1" />
              Tags
            </label>
            <div className="space-y-3">
              {/* Tag Input */}
              <input
                type="text"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                placeholder="Type tags with # (e.g., #financials #compliance) or select from existing"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* Selected Tags */}
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map(tagId => {
                    const tag = tags.find(t => t.id === tagId);
                    return tag ? (
                      <span
                        key={tagId}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => handleTagToggle(tagId)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              
              {/* Available Tags */}
              <div className="flex flex-wrap gap-2">
                {tags.filter(tag => !selectedTags.includes(tag.id)).map(renderTagButton)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fileUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Resource' : 'Update Resource'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

